import { Component } from '@theme/component';

/**
 * Content Carousel
 *
 * A lightweight Swiper wrapper for a horizontal track of static content cards
 * (image + title + text). Navigation buttons live in the section header, so we
 * pass explicit prev/next elements to Swiper rather than relying on defaults.
 */
class ContentCarousel extends Component {
  /** @type {any} */
  #swiper = null;

  connectedCallback() {
    super.connectedCallback();
    queueMicrotask(() => this.#init());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    try {
      this.#swiper?.destroy?.(true, true);
    } catch (_) {}
    this.#swiper = null;
  }

  async #init() {
    const container = this.querySelector('.swiper');
    if (!(container instanceof HTMLElement) || container.dataset.swiperInitialized === 'true') return;

    const Swiper = await this.#waitForSwiper();
    if (!Swiper) return;

    const prevEl = this.querySelector('.content-carousel__btn--prev');
    const nextEl = this.querySelector('.content-carousel__btn--next');

    // Slide width (cards per view + edge peek) is CSS-driven via the section's
    // --carousel-* custom properties; 'auto' makes Swiper read it from layout.
    this.#swiper = new Swiper(container, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      watchOverflow: true,
      keyboard: { enabled: true },
      a11y: { enabled: true },
      ...(prevEl && nextEl ? { navigation: { prevEl, nextEl } } : {}),
    });

    container.dataset.swiperInitialized = 'true';
  }

  /**
   * Wait for the global Swiper to be available (loaded as a separate module).
   * @returns {Promise<any | null>}
   */
  #waitForSwiper(retries = 10, interval = 50) {
    return new Promise((resolve) => {
      const attempt = (remaining) => {
        // @ts-ignore
        const SwiperGlobal = window.Swiper;
        if (SwiperGlobal) return resolve(SwiperGlobal);
        if (remaining <= 0) return resolve(null);
        setTimeout(() => attempt(remaining - 1), interval);
      };
      attempt(retries);
    });
  }
}

if (!customElements.get('content-carousel')) {
  customElements.define('content-carousel', ContentCarousel);
}
